/**
 * PremiumWizard.jsx
 * Formulário em etapas com visual premium
 *
 * Features:
 * - Stepper visual com ícones
 * - Navegação por teclado (Enter = próximo, Escape = cancelar)
 * - Validação por step
 * - Resumo lateral (desktop)
 * - Persistência de rascunho (localStorage)
 * - Animações de transição
 * - Suporte a steps opcionais
 *
 * Usage:
 * ```jsx
 * const steps = [
 *   {
 *     label: 'Dados Básicos',
 *     title: 'Informações do Veículo',
 *     description: 'Preencha os dados básicos',
 *     icon: <DirectionsCarIcon />,
 *     component: Step1Component,
 *     validate: (data) => data.name ? true : { name: 'Nome é obrigatório' },
 *     getSummary: (data) => [{ label: 'Nome', value: data.name }],
 *   },
 *   // ...more steps
 * ];
 *
 * <PremiumWizard
 *   steps={steps}
 *   onComplete={(data) => console.log('Done!', data)}
 *   onCancel={() => navigate(-1)}
 *   storageKey="device-wizard-draft"
 * />
 * ```
 */

import React, {
 useState, useCallback, useEffect, useMemo 
} from 'react';
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ChevronLeft as BackIcon,
  ChevronRight as NextIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../../theme/usePremiumTokens';
import { useTranslation } from '../LocalizationProvider';
import PremiumButton from './PremiumButton';
import './premiumWizard.css';

// ============================================================================
// STEPPER COMPONENT
// ============================================================================

const WizardStepper = ({
  steps,
  currentStep,
  onStepClick,
  completedSteps,
  tokens,
  t,
}) => {
  const colors = {
    completed: tokens?.accent?.primary || '#00d4aa',
    current: tokens?.accent?.primary || '#00d4aa',
    pending: tokens?.text?.muted || '#6e7681',
    line: tokens?.border?.subtle || 'rgba(255,255,255,0.1)',
    lineFilled: tokens?.accent?.primary || '#00d4aa',
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        gap: 0,
        mb: 4,
        px: 2,
        flexWrap: 'wrap',
      }}
    >
      {steps.map((step, index) => {
        const isCompleted = completedSteps.includes(index);
        const isCurrent = index === currentStep;
        const isPending = !isCompleted && !isCurrent;

        return (
          <React.Fragment key={index}>
            {/* Step Circle + Label */}
            <Box
              onClick={() => onStepClick?.(index)}
              className={`wizard-step ${isCurrent ? 'wizard-step-current' : ''}`}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
                opacity: isPending ? 0.6 : 1,
                transition: 'all 0.3s ease',
                backgroundColor: isCurrent ? (tokens?.accent?.subtle || 'rgba(0, 212, 170, 0.08)') : 'transparent',
                borderRadius: '12px',
                padding: '12px 16px',
                margin: '0 4px',
                '&:hover': {
                  opacity: 1,
                  transform: 'scale(1.05)',
                  backgroundColor: isCurrent ? (tokens?.accent?.hover || 'rgba(0, 212, 170, 0.12)') : 'rgba(255, 255, 255, 0.04)',
                },
              }}
            >
              {/* Circle */}
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isCompleted
                    ? colors.completed
                    : isCurrent
                      ? `${colors.current}20`
                      : 'transparent',
                  border: `2px solid ${
                    isCompleted || isCurrent ? colors.completed : colors.pending
                  }`,
                  color: isCompleted
                    ? tokens?.text?.inverse || '#ffffff'
                    : isCurrent
                      ? colors.current
                      : colors.pending,
                  fontWeight: 600,
                  fontSize: '14px',
                  transition: 'all 0.3s ease',
                  boxShadow: isCurrent ? `0 0 0 4px ${colors.current}20` : 'none',
                }}
              >
                {isCompleted ? (
                  <CheckIcon sx={{ fontSize: 20 }} />
                ) : (
                  step.icon || index + 1
                )}
              </Box>

              {/* Label */}
              <Typography
                variant="caption"
                sx={{
                  mt: 1,
                  color: isCurrent ? colors.current : colors.pending,
                  fontWeight: isCurrent ? 600 : 400,
                  textAlign: 'center',
                  maxWidth: 80,
                  fontSize: '11px',
                  lineHeight: 1.2,
                }}
              >
                {step.label}
              </Typography>

              {/* Optional badge */}
              {step.optional && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '9px',
                    color: colors.pending,
                    opacity: 0.7,
                  }}
                >
                  ({t('wizardOptional')})
                </Typography>
              )}
            </Box>

            {/* Connector Line */}
            {index < steps.length - 1 && (
              <Box
                sx={{
                  flex: '1 1 auto',
                  minWidth: 30,
                  maxWidth: 80,
                  height: 2,
                  mx: 1,
                  mt: 2.5, // Align with center of circles
                  backgroundColor: isCompleted ? colors.lineFilled : colors.line,
                  transition: 'background-color 0.5s ease',
                  borderRadius: 1,
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

// ============================================================================
// PROGRESS BAR COMPONENT
// ============================================================================

const WizardProgressBar = ({ current, total, tokens, t }) => {
  const progress = ((current + 1) / total) * 100;

  return (
    <Box sx={{ width: '100%', mb: 3 }}>
      <Box
        sx={{
          height: 4,
          borderRadius: 2,
          backgroundColor: tokens?.border?.subtle || 'rgba(255,255,255,0.1)',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: `${progress}%`,
            backgroundColor: tokens?.accent?.primary || '#00d4aa',
            borderRadius: 2,
            transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />
      </Box>
      <Typography
        variant="caption"
        sx={{
          color: tokens?.text?.muted || '#6e7681',
          mt: 0.5,
          display: 'block',
          textAlign: 'right',
        }}
      >
        {t('wizardStepOf').replace('{0}', current + 1).replace('{1}', total)}
      </Typography>
    </Box>
  );
};

// ============================================================================
// MOBILE STEP INDICATOR COMPONENT
// ============================================================================

const MobileStepIndicator = ({
  currentStep,
  totalSteps,
  stepTitle,
  tokens,
  t,
}) => {
  const progress = ((currentStep + 1) / totalSteps) * 100;

  return (
    <Box
      className="wizard-mobile-indicator"
      sx={{
        mb: 2,
        p: 2,
        backgroundColor: tokens?.surface?.elevated || '#161b22',
        borderRadius: '12px',
        border: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.1)'}`,
      }}
    >
      {/* Step text */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography
          sx={{
            color: tokens?.accent?.primary || '#00d4aa',
            fontSize: '12px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {t('wizardStepOf').replace('{0}', currentStep + 1).replace('{1}', totalSteps)}
        </Typography>
        <Typography
          sx={{
            color: tokens?.text?.muted || '#6e7681',
            fontSize: '11px',
          }}
        >
          {Math.round(progress)}%
        </Typography>
      </Box>

      {/* Step title */}
      <Typography
        sx={{
          color: tokens?.text?.primary || '#f0f6fc',
          fontSize: '16px',
          fontWeight: 600,
          mb: 1.5,
        }}
      >
        {stepTitle}
      </Typography>

      {/* Progress bar */}
      <Box
        sx={{
          height: 4,
          borderRadius: 2,
          backgroundColor: tokens?.border?.subtle || 'rgba(255,255,255,0.1)',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: `${progress}%`,
            backgroundColor: tokens?.accent?.primary || '#00d4aa',
            borderRadius: 2,
            transition: 'width 0.3s ease',
          }}
        />
      </Box>
    </Box>
  );
};

// ============================================================================
// SUMMARY SIDEBAR COMPONENT
// ============================================================================

const WizardSummary = ({ items, tokens, t }) => {
  if (!items || items.length === 0) return null;

  const colors = {
    bg: tokens?.surface?.elevated || '#161b22',
    border: tokens?.border?.subtle || 'rgba(255,255,255,0.1)',
    text: tokens?.text?.primary || '#f0f6fc',
    muted: tokens?.text?.muted || '#6e7681',
  };

  return (
    <Box
      sx={{
        width: 280,
        flexShrink: 0,
        backgroundColor: colors.bg,
        borderRadius: '12px',
        border: `1px solid ${colors.border}`,
        p: 2,
        alignSelf: 'flex-start',
        position: 'sticky',
        top: 20,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          color: colors.muted,
          mb: 2,
          fontWeight: 600,
          textTransform: 'uppercase',
          fontSize: '11px',
          letterSpacing: '0.05em',
        }}
      >
        {t('wizardSummary')}
      </Typography>

      {items.map((item, index) => (
        <Box
          key={index}
          sx={{
            mb: 1.5,
            pb: 1.5,
            borderBottom: index < items.length - 1
              ? `1px solid ${colors.border}`
              : 'none',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: colors.muted,
              display: 'block',
              fontSize: '10px',
              textTransform: 'uppercase',
              letterSpacing: '0.03em',
            }}
          >
            {item.label}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: colors.text,
              fontWeight: 500,
              mt: 0.25,
            }}
          >
            {item.value || '-'}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// ============================================================================
// MAIN WIZARD COMPONENT
// ============================================================================

const PremiumWizard = ({
  steps,
  onComplete,
  onCancel,
  initialStep = 0,
  initialData = {},
  storageKey = null,
  showSummary = true,
  showProgressBar = true,
  showStepper = true,
  allowSkipOptional = true,
  completeButtonText = 'Concluir',
  nextButtonText = 'Próximo',
  backButtonText = 'Voltar',
  cancelButtonText = 'Cancelar',
  skipButtonText = 'Pular',
  className,
  sx,
}) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [data, setData] = useState(initialData);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [errors, setErrors] = useState({});
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationDirection, setAnimationDirection] = useState('forward');

  const currentStepConfig = steps[currentStep];

  // Restore from localStorage
  useEffect(() => {
    if (storageKey) {
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed.data) setData(parsed.data);
          if (parsed.completedSteps) setCompletedSteps(parsed.completedSteps);
          if (typeof parsed.currentStep === 'number') setCurrentStep(parsed.currentStep);
        }
      } catch (e) {
        console.warn('Failed to restore wizard state:', e);
      }
    }
  }, [storageKey]);

  // Save to localStorage
  useEffect(() => {
    if (storageKey && Object.keys(data).length > 0) {
      try {
        localStorage.setItem(storageKey, JSON.stringify({
          data,
          completedSteps,
          currentStep,
          timestamp: Date.now(),
        }));
      } catch (e) {
        console.warn('Failed to save wizard state:', e);
      }
    }
  }, [data, completedSteps, currentStep, storageKey]);

  // Validate current step
  const validateCurrentStep = useCallback(() => {
    if (!currentStepConfig.validate) return true;

    const result = currentStepConfig.validate(data);

    if (result === true || result === undefined) {
      setErrors({});
      return true;
    }

    if (typeof result === 'string') {
      setErrors({ general: result });
      return false;
    }

    setErrors(result);
    return false;
  }, [currentStepConfig, data]);

  // Navigate to step
  const goToStep = useCallback((stepIndex, skipValidation = false) => {
    if (stepIndex < 0 || stepIndex >= steps.length) return;

    // Validate before moving forward
    if (stepIndex > currentStep && !skipValidation && !validateCurrentStep()) {
      return;
    }

    setAnimationDirection(stepIndex > currentStep ? 'forward' : 'backward');
    setIsAnimating(true);

    setTimeout(() => {
      setCurrentStep(stepIndex);
      setIsAnimating(false);
    }, 150);
  }, [currentStep, steps.length, validateCurrentStep]);

  // Go to next step
  const goNext = useCallback(() => {
    // Skip validation for optional steps if allowed
    const isOptional = currentStepConfig.optional && allowSkipOptional;
    if (!isOptional && !validateCurrentStep()) return;

    // Mark step as completed
    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps(prev => [...prev, currentStep]);
    }

    if (currentStep === steps.length - 1) {
      // Last step - complete wizard
      if (onComplete) {
        onComplete(data);
      }
      if (storageKey) {
        localStorage.removeItem(storageKey);
      }
    } else {
      goToStep(currentStep + 1, true);
    }
  }, [
    currentStep,
    steps.length,
    validateCurrentStep,
    completedSteps,
    data,
    onComplete,
    storageKey,
    goToStep,
    currentStepConfig.optional,
    allowSkipOptional,
  ]);

  // Go to previous step
  const goBack = useCallback(() => {
    if (currentStep > 0) {
      goToStep(currentStep - 1, true);
    }
  }, [currentStep, goToStep]);

  // Skip optional step
  const skipStep = useCallback(() => {
    if (currentStepConfig.optional && currentStep < steps.length - 1) {
      goToStep(currentStep + 1, true);
    }
  }, [currentStepConfig.optional, currentStep, steps.length, goToStep]);

  // Update data
  const updateData = useCallback((newData) => {
    setData(prev => ({ ...prev, ...newData }));
    setErrors({});
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't intercept if user is typing in an input
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) {
        if (e.key === 'Escape') {
          e.target.blur();
        }
        return;
      }

      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        goNext();
      } else if (e.key === 'Escape') {
        if (onCancel) onCancel();
      } else if (e.key === 'ArrowLeft' && e.altKey) {
        e.preventDefault();
        goBack();
      } else if (e.key === 'ArrowRight' && e.altKey) {
        e.preventDefault();
        goNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goNext, goBack, onCancel]);

  // Colors
  const colors = {
    bg: tokens?.surface?.elevated || '#161b22',
    bgRaised: tokens?.surface?.raised || '#1c2128',
    border: tokens?.border?.subtle || 'rgba(255,255,255,0.1)',
    text: tokens?.text?.primary || '#f0f6fc',
    muted: tokens?.text?.muted || '#6e7681',
    accent: tokens?.accent?.primary || '#00d4aa',
    error: tokens?.semantic?.error || '#f85149',
    errorBg: tokens?.semantic?.errorBg || 'rgba(248, 81, 73, 0.1)',
  };

  // Summary items
  const summaryItems = useMemo(() => {
    return steps
      .filter(step => step.getSummary)
      .map(step => step.getSummary(data))
      .filter(Boolean)
      .flat();
  }, [steps, data]);

  // Animation styles
  const contentAnimationSx = {
    opacity: isAnimating ? 0 : 1,
    transform: isAnimating
      ? animationDirection === 'forward'
        ? 'translateX(30px)'
        : 'translateX(-30px)'
      : 'translateX(0)',
    transition: 'all 0.15s ease-out',
  };

  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: 3,
        minHeight: 400,
        ...sx,
      }}
    >
      {/* Main Content */}
      <Box
        className="wizard-main-content"
        sx={{
          flex: 1,
          minWidth: 0,
          // Add padding at bottom on mobile for fixed buttons
          pb: isMobile ? '100px' : 0,
        }}
      >
        {/* Mobile Step Indicator - compact version for small screens */}
        {isMobile && showStepper && (
          <MobileStepIndicator
            currentStep={currentStep}
            totalSteps={steps.length}
            stepTitle={currentStepConfig.title || currentStepConfig.label}
            tokens={tokens}
            t={t}
          />
        )}

        {/* Desktop Stepper - full version with circles and labels */}
        {!isMobile && showStepper && (
          <WizardStepper
            steps={steps}
            currentStep={currentStep}
            completedSteps={completedSteps}
            onStepClick={goToStep}
            tokens={tokens}
            t={t}
          />
        )}

        {/* Progress Bar - only on desktop (mobile has it in MobileStepIndicator) */}
        {!isMobile && showProgressBar && (
          <WizardProgressBar
            current={currentStep}
            total={steps.length}
            tokens={tokens}
            t={t}
          />
        )}

        {/* Step Content */}
        <Box
          sx={{
            backgroundColor: colors.bg,
            borderRadius: '12px',
            border: `1px solid ${colors.border}`,
            p: 3,
            minHeight: 300,
            ...contentAnimationSx,
          }}
        >
          {/* Step Title */}
          <Typography
            variant="h6"
            sx={{
              color: colors.text,
              fontWeight: 600,
              mb: 0.5,
            }}
          >
            {currentStepConfig.title || currentStepConfig.label}
          </Typography>

          {/* Step Description */}
          {currentStepConfig.description && (
            <Typography
              variant="body2"
              sx={{ color: colors.muted, mb: 3 }}
            >
              {currentStepConfig.description}
            </Typography>
          )}

          {/* Error Message */}
          {errors.general && (
            <Box
              sx={{
                p: 2,
                mb: 2,
                borderRadius: '8px',
                backgroundColor: colors.errorBg,
                border: `1px solid ${colors.error}30`,
              }}
            >
              <Typography sx={{ color: colors.error, fontSize: '14px' }}>
                {errors.general}
              </Typography>
            </Box>
          )}

          {/* Step Component */}
          {currentStepConfig.component && (
            <currentStepConfig.component
              data={data}
              updateData={updateData}
              errors={errors}
              setErrors={setErrors}
            />
          )}

          {/* Render function alternative */}
          {currentStepConfig.render && currentStepConfig.render({
            data,
            updateData,
            errors,
            setErrors,
          })}
        </Box>

        {/* Navigation Buttons */}
        <Box
          className="wizard-buttons"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 3,
            gap: 2,
            flexWrap: 'wrap',
            // Mobile: fixed at bottom
            ...(isMobile && {
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              mt: 0,
              p: 2,
              pb: 'calc(16px + env(safe-area-inset-bottom, 0px))',
              backgroundColor: colors.bg,
              borderTop: `1px solid ${colors.border}`,
              zIndex: 100,
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              boxShadow: '0 -4px 24px rgba(0, 0, 0, 0.3)',
            }),
          }}
        >
          {/* Left side - Cancel (hidden on mobile to save space) */}
          <Box sx={{ display: isMobile ? 'none' : 'block' }}>
            {onCancel && (
              <PremiumButton
                variant="ghost"
                onClick={onCancel}
                size="md"
              >
                {cancelButtonText}
              </PremiumButton>
            )}
          </Box>

          {/* Right side - Back, Skip, Next */}
          <Box
            sx={{
              display: 'flex',
              gap: isMobile ? 1.5 : 2,
              // Mobile: full width with flex
              ...(isMobile && {
                width: '100%',
                justifyContent: 'space-between',
              }),
            }}
          >
            {currentStep > 0 ? (
              <PremiumButton
                variant="secondary"
                onClick={goBack}
                startIcon={<BackIcon />}
                size={isMobile ? 'sm' : 'md'}
                sx={isMobile ? { flex: 1 } : {}}
              >
                {backButtonText}
              </PremiumButton>
            ) : (
              // Show cancel on mobile when no back button
              isMobile && onCancel && (
                <PremiumButton
                  variant="ghost"
                  onClick={onCancel}
                  size="sm"
                  sx={{ flex: 1 }}
                >
                  {cancelButtonText}
                </PremiumButton>
              )
            )}

            {currentStepConfig.optional && allowSkipOptional && currentStep < steps.length - 1 && !isMobile && (
              <PremiumButton
                variant="ghost"
                onClick={skipStep}
                size="md"
              >
                {skipButtonText}
              </PremiumButton>
            )}

            <PremiumButton
              variant="primary"
              onClick={goNext}
              endIcon={currentStep < steps.length - 1 ? <NextIcon /> : <CheckIcon />}
              size={isMobile ? 'sm' : 'md'}
              className="wizard-next-button"
              sx={{
                minWidth: isMobile ? 0 : 130,
                flex: isMobile ? 1.5 : 'none',
                color: `${tokens?.text?.inverse || '#ffffff'} !important`,
                fontWeight: '600 !important',
                '& .MuiButton-endIcon': { color: `${tokens?.text?.inverse || '#ffffff'} !important` },
                '& .MuiButton-startIcon': { color: `${tokens?.text?.inverse || '#ffffff'} !important` },
              }}
            >
              {currentStep === steps.length - 1 ? completeButtonText : nextButtonText}
            </PremiumButton>
          </Box>
        </Box>

        {/* Keyboard hints - hidden on mobile */}
        {!isMobile && (
          <Typography
            variant="caption"
            sx={{
              color: colors.muted,
              mt: 2,
              display: 'block',
              textAlign: 'center',
              opacity: 0.6,
            }}
          >
            {t('wizardPressEnter')}
          </Typography>
        )}
      </Box>

      {/* Summary Sidebar (Desktop only) */}
      {showSummary && !isMobile && summaryItems.length > 0 && (
        <WizardSummary items={summaryItems} tokens={tokens} t={t} />
      )}
    </Box>
  );
};

// ============================================================================
// EXPORTS
// ============================================================================

export default PremiumWizard;

// Export sub-components for custom implementations
export {
  WizardStepper,
  WizardProgressBar,
  WizardSummary,
  MobileStepIndicator,
};
